import { AccountRow } from "@/components/accountSwitcher/AccountRow.tsx";
import {
  getProviderLabel,
  useAccountActions,
} from "@/components/accountSwitcher/useAccountActions.ts";
import { panelHeaderControlClass } from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { AvatarWithFallback } from "@/components/ui/avatar.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { infoToast } from "@/components/ui/toast.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { cn } from "@/lib/utils.ts";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";

/**
 * Where the switcher is sitting, which decides how much of itself it shows.
 * "chip" is the header trigger, and shows the avatar alone at every width —
 * the row it lands in already names the screen. "row" is the sidebar footer:
 * full width, with the account label and a chevron always on.
 */
export type AccountSwitcherVariant = "chip" | "row";

export const AccountSwitcher = ({ variant = "chip" }: { variant?: AccountSwitcherVariant }) => {
  const [open, setOpen] = useState(false);
  const { userContext } = useCurrentUserContext();
  // Land on home after a switch: the dropdown lives on the homepage, and "/"
  // drops any account-scoped query params (see useAccountActions). The hook is
  // still what mounts the add-account popup-result listener; adding accounts
  // lives in Settings → Accounts, but a popup completed while the user is on
  // the homepage still needs to be caught here.
  const { switchMutation } = useAccountActions({ postSwitchPath: "/" });

  if (!userContext) return null;

  const { user } = userContext;
  const currentAccountId = user.id;
  const currentAvatarValue = user.userContact.accountEmail ?? user.userContact.name;
  const currentName = user.userContact.name;
  const currentPicture = user.userContact.picture;

  const accounts = user.accounts ?? [];
  const currentEntry = accounts.find((a) => a.id === currentAccountId);
  const otherAccounts = accounts.filter((a) => a.id !== currentAccountId);

  const currentChipLabel = currentEntry?.displayName ?? currentName;

  const close = () => setOpen(false);

  const isRow = variant === "row";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={isRow ? "ghost" : "secondary"}
          className={cn(
            "[&[data-state=open]_.chevron]:rotate-180",
            isRow
              ? // A row in the sidebar's list, not a header control: it takes
                // the thread row's own metrics, down to the `-mx-2` / `px-2`
                // pair that bleeds the hover surface into the gutter while
                // leaving the avatar on the rows' icon column.
                //
                // `min-w-0` with no `flex-1` keeps the button content-width, so
                // the chevron sits a fixed `gap-2` from the label and truncates
                // it rather than colliding with the gear. `border-0` strips the
                // Button base's 1px transparent border, which otherwise stands
                // the avatar 1px inside that icon column.
                "-mx-2 h-9 min-w-0 gap-2 rounded-lg border-0 bg-transparent px-2 font-normal transition-colors hover:bg-popover"
              : // A header control like the ones beside it, and only ever on a
                // phone — `Calendar` builds this variant behind `isMobile`. The
                // avatar is the whole button: the row it lands in already names
                // the screen, and an account label there cost most of the width
                // the title needed while leaving this reading as a pill among
                // glyphs rather than one of them.
                cn(panelHeaderControlClass, "rounded-full border-0 bg-transparent p-0"),
          )}
          data-statsig-name="home-account-switcher"
          aria-label={`Current account: ${currentChipLabel}. Switch account.`}
        >
          <AvatarWithFallback
            size="xs"
            className="shrink-0 border-0"
            person={{
              name: currentName,
              picture: currentPicture,
              value: currentAvatarValue,
            }}
          />
          {isRow && (
            <>
              <span className="min-w-0 truncate whitespace-nowrap text-left text-sm">
                {currentChipLabel}
              </span>
              <ChevronDown className="chevron size-4 shrink-0 text-muted-foreground transition-transform duration-200" />
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2">
        <AccountRow
          primary={currentChipLabel}
          avatarName={currentEntry?.displayName ?? currentName}
          picture={currentEntry?.picture ?? currentPicture ?? undefined}
          secondary={currentEntry ? getProviderLabel(currentEntry.provider) : undefined}
          isCurrent
          statsigName="home-account-switcher-current"
          onClick={() => {
            infoToast("You're already on this account.");
            close();
          }}
          action={
            <Button
              variant="outlinePopover"
              size="sm"
              className="h-7 px-2 text-xs shrink-0 hover:bg-card"
              data-statsig-name="account-switcher-settings"
              asChild
              onClick={(e) => {
                // Don't fire the row's "already on this account" toast when the user
                // clicks Manage — the click bubbles up otherwise.
                e.stopPropagation();
                close();
              }}
            >
              <Link href="/settings/account">Manage</Link>
            </Button>
          }
        />
        {otherAccounts.map((acct) => {
          const primary = acct.displayName ?? getProviderLabel(acct.provider);
          return (
            <AccountRow
              key={acct.id}
              primary={primary}
              avatarName={acct.displayName ?? primary}
              picture={acct.picture ?? undefined}
              secondary={getProviderLabel(acct.provider)}
              isCurrent={false}
              disabled={switchMutation.isPending}
              statsigName="home-account-switcher-switch"
              onClick={() => {
                switchMutation.mutate({ accountId: acct.id, displayName: acct.displayName });
                close();
              }}
            />
          );
        })}
      </PopoverContent>
    </Popover>
  );
};
